/**
 * NearbyPanel — offcanvas UI for the mesh layer.
 *
 * Shows:
 *   - Local nickname with an edit control
 *   - Live peer count + peer list (nickname, short ID, last-seen, transport)
 *   - A broadcast chat input + running message log
 *
 * The panel is populated into a Bootstrap offcanvas (#offcanvasNearby).
 * The component wires itself to a MeshClient instance via init(meshClient).
 */

import { Offcanvas } from 'bootstrap';

const PANEL_ID = 'offcanvasNearby';
const MAX_MESSAGES = 100;

let mesh = null;
let unsubs = [];
let offcanvasInstance = null;
let messages = []; // { senderNickname, text, ts, self? }

// DOM refs (filled in init)
let el = {
  panel: null,
  selfNick: null,
  editNickBtn: null,
  peerCount: null,
  peerList: null,
  statusBadge: null,
  transportList: null,
  messageLog: null,
  messageInput: null,
  sendBtn: null,
  bleControls: null,
  scanBtn: null,
  scanBtnLabel: null,
  bleStatus: null,
};

// ---------- public API ----------

/**
 * Initialise the Nearby panel and wire it to a MeshClient.
 */
export function initNearbyPanel(meshClient) {
  mesh = meshClient;

  el.panel = document.getElementById(PANEL_ID);
  if (!el.panel) {
    console.warn('[NearbyPanel] #offcanvasNearby not found in DOM');
    return;
  }

  _renderShell();
  _cacheDomRefs();
  _bindDomEvents();
  _subscribeMesh();
  _refreshSelf();
  _refreshPeerList();
  _refreshTransportList();

  offcanvasInstance = new Offcanvas(el.panel);
  console.log('[NearbyPanel] Ready');
}

/** Programmatically open the panel. */
export function showNearbyPanel() {
  offcanvasInstance?.show();
}

/** Tear down (used only by tests / hot reload). */
export function destroyNearbyPanel() {
  for (const u of unsubs) { try { u(); } catch (_) {} }
  unsubs = [];
  offcanvasInstance?.dispose();
  offcanvasInstance = null;
  mesh = null;
  messages = [];
}

// ---------- render ----------

function _renderShell() {
  el.panel.innerHTML = `
    <div class="offcanvas-header">
      <h5 class="offcanvas-title">
        <i class="bi bi-broadcast me-2"></i>
        <span>Nearby</span>
        <span class="badge bg-secondary ms-2 nearby-status-badge">idle</span>
      </h5>
      <button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
    </div>
    <div class="offcanvas-body p-0 d-flex flex-column" style="overflow:hidden">

      <!-- Self -->
      <div class="p-3 border-bottom bg-light">
        <div class="small text-muted">You</div>
        <div class="d-flex align-items-center gap-2">
          <strong class="nearby-self-nick">—</strong>
          <button class="btn btn-sm btn-link p-0 nearby-edit-nick" type="button" title="Change nickname">
            <i class="bi bi-pencil"></i>
          </button>
        </div>
        <div class="small text-muted nearby-transport-list">No transport active</div>
        <div class="mt-2 nearby-ble-controls d-none">
          <button class="btn btn-outline-primary btn-sm nearby-scan-btn" type="button">
            <i class="bi bi-bluetooth me-1"></i><span class="nearby-scan-btn-label">Scan BLE</span>
          </button>
          <span class="ms-2 small nearby-ble-status text-muted">not connected</span>
        </div>
      </div>

      <!-- Peers -->
      <div class="px-3 pt-3 pb-1 d-flex align-items-center justify-content-between">
        <div class="fw-semibold">Peers nearby <span class="text-muted">(<span class="nearby-peer-count">0</span>)</span></div>
      </div>
      <div class="nearby-peer-list px-3 pb-2" style="max-height:30vh; overflow:auto;">
        <div class="text-muted small fst-italic">No peers detected yet. Open BisaWofa in a second tab to simulate one.</div>
      </div>

      <!-- Messages -->
      <div class="px-3 pt-2 pb-1 border-top fw-semibold">Broadcast chat</div>
      <div class="nearby-message-log px-3 flex-grow-1" style="overflow:auto; min-height:120px;"></div>

      <!-- Input -->
      <div class="p-2 border-top d-flex gap-2">
        <input type="text" class="form-control form-control-sm nearby-message-input"
               placeholder="Broadcast to nearby peers…" maxlength="240">
        <button class="btn btn-primary btn-sm nearby-send-btn" type="button">
          <i class="bi bi-send"></i>
        </button>
      </div>
    </div>
  `;
}

function _cacheDomRefs() {
  el.selfNick       = el.panel.querySelector('.nearby-self-nick');
  el.editNickBtn    = el.panel.querySelector('.nearby-edit-nick');
  el.peerCount      = el.panel.querySelector('.nearby-peer-count');
  el.peerList       = el.panel.querySelector('.nearby-peer-list');
  el.statusBadge    = el.panel.querySelector('.nearby-status-badge');
  el.transportList  = el.panel.querySelector('.nearby-transport-list');
  el.messageLog     = el.panel.querySelector('.nearby-message-log');
  el.messageInput   = el.panel.querySelector('.nearby-message-input');
  el.sendBtn        = el.panel.querySelector('.nearby-send-btn');
  el.bleControls    = el.panel.querySelector('.nearby-ble-controls');
  el.scanBtn        = el.panel.querySelector('.nearby-scan-btn');
  el.scanBtnLabel   = el.panel.querySelector('.nearby-scan-btn-label');
  el.bleStatus      = el.panel.querySelector('.nearby-ble-status');
}

// ---------- DOM ↔ mesh bindings ----------

function _bindDomEvents() {
  el.sendBtn.addEventListener('click', _sendCurrentInput);
  el.messageInput.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter' && !ev.shiftKey) {
      ev.preventDefault();
      _sendCurrentInput();
    }
  });
  el.editNickBtn.addEventListener('click', _editNickname);
  el.scanBtn.addEventListener('click', _scanBluetooth);
}

async function _scanBluetooth() {
  if (!mesh) return;
  el.scanBtnLabel.textContent = 'Scanning…';
  el.scanBtn.disabled = true;
  el.bleStatus.textContent = 'choose a device';
  el.bleStatus.classList.remove('text-success', 'text-danger', 'text-muted');
  try {
    const { deviceName } = await mesh.scanWebBluetooth();
    el.scanBtnLabel.textContent = 'Rescan';
    el.bleStatus.innerHTML = `connected to <strong>${_esc(deviceName)}</strong>`;
    el.bleStatus.classList.add('text-success');
  } catch (err) {
    console.warn('[NearbyPanel] BLE scan aborted:', err.message);
    el.scanBtnLabel.textContent = 'Scan BLE';

    // Translate common failure modes into something actionable for the user
    const msg = String(err.message || err.name || '');
    let friendly;
    if (err.name === 'NotFoundError' || /cancell?ed/i.test(msg)) {
      friendly = 'no device selected';
    } else if (/globally disabled|permission.*blocked/i.test(msg)) {
      friendly = 'Web Bluetooth blocked by browser. See console for fix.';
      console.warn('[NearbyPanel] Web Bluetooth is disabled at the browser level.\n' +
                   '  Brave:  enable at  brave://settings/content/bluetoothDevices\n' +
                   '          (or disable Shields for localhost)\n' +
                   '  Chrome: enabled by default — try launching BisaWofa in Chrome instead.');
    } else if (/user.*denied|permission.*denied/i.test(msg)) {
      friendly = 'permission denied by user';
    } else {
      friendly = msg;
    }
    el.bleStatus.textContent = friendly;
    el.bleStatus.classList.add('text-danger');
  } finally {
    el.scanBtn.disabled = false;
  }
}

function _subscribeMesh() {
  unsubs.push(mesh.onPeer(() => _refreshPeerList()));
  unsubs.push(mesh.onPeerLost(() => _refreshPeerList()));
  unsubs.push(mesh.onMessage((env) => _appendMessage(env)));
  unsubs.push(mesh.onStatus((status) => _applyStatus(status)));

  // NearbyPanel is typically initialised AFTER MeshClient.init() already
  // emitted its status events, so apply the current state immediately so the
  // badge isn't frozen at the HTML default.
  _applyStatus(mesh.state || 'idle');
}

function _applyStatus(status) {
  el.statusBadge.textContent = status;
  el.statusBadge.className = 'badge ms-2 nearby-status-badge ' +
    (status === 'running' ? 'bg-success' :
     status === 'starting' ? 'bg-info' :
     status === 'stopping' ? 'bg-warning' : 'bg-secondary');
  if (status === 'running') _refreshTransportList();
}

async function _sendCurrentInput() {
  const text = el.messageInput.value.trim();
  if (!text || !mesh) return;
  el.messageInput.value = '';
  try {
    await mesh.broadcast(text);
    _appendMessage({
      senderNickname: mesh.getSelf()?.nickname || 'You',
      senderIdHex:    mesh.getSelf()?.peerIdHex || '',
      type:           'broadcast',
      text,
      timestamp:      Date.now(),
      self:           true,
    });
  } catch (err) {
    console.error('[NearbyPanel] Broadcast failed:', err);
  }
}

async function _editNickname() {
  const current = mesh.getSelf()?.nickname || '';
  const next = window.prompt('Your mesh nickname:', current);
  if (!next || next === current) return;
  const clean = next.trim().slice(0, 32);
  if (!clean) return;
  try {
    await mesh.setNickname(clean);
    _refreshSelf();
  } catch (err) {
    console.error('[NearbyPanel] Nickname update failed:', err);
  }
}

// ---------- render updates ----------

function _refreshSelf() {
  const self = mesh?.getSelf();
  if (!self) { el.selfNick.textContent = '—'; return; }
  el.selfNick.textContent = self.nickname;
  el.selfNick.title = self.peerIdHex;
}

function _refreshPeerList() {
  const peers = mesh?.getPeers() || [];
  el.peerCount.textContent = String(peers.length);
  if (!peers.length) {
    el.peerList.innerHTML = '<div class="text-muted small fst-italic">No peers detected yet. Open BisaWofa in a second tab to simulate one.</div>';
    return;
  }
  const sorted = peers.slice().sort((a, b) => b.lastSeen - a.lastSeen);
  el.peerList.innerHTML = sorted.map(_peerRow).join('');
}

function _peerRow(peer) {
  const shortId = peer.idHex.slice(0, 8);
  const ago = _ago(peer.lastSeen);
  const safeNick = _esc(peer.nickname);
  const safeTransport = _esc(peer.transport || '');
  return `
    <div class="d-flex align-items-center justify-content-between py-2 border-bottom">
      <div>
        <div><strong>${safeNick}</strong> <span class="text-muted small">#${shortId}</span></div>
        <div class="small text-muted">${safeTransport} · seen ${ago}</div>
      </div>
    </div>
  `;
}

function _refreshTransportList() {
  if (!mesh || !mesh.transports.length) {
    el.transportList.textContent = 'No transport active';
    el.bleControls?.classList.add('d-none');
    return;
  }
  const names = mesh.transports.map(t => t.capabilities.name);
  el.transportList.textContent = 'Transports: ' + names.join(', ');
  const hasBle = mesh.transports.some(t =>
    t.capabilities.name.startsWith('Web Bluetooth') ||
    t.capabilities.name.startsWith('Capacitor BLE')
  );
  if (hasBle) el.bleControls?.classList.remove('d-none');
  else el.bleControls?.classList.add('d-none');
}

function _appendMessage(env) {
  messages.push(env);
  if (messages.length > MAX_MESSAGES) messages = messages.slice(-MAX_MESSAGES);
  const safeNick = _esc(env.senderNickname || 'unknown');
  const safeText = _esc(env.text || '');
  const time = new Date(env.timestamp || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const rowClass = env.self ? 'text-end' : '';
  const bubbleClass = env.self ? 'bg-primary text-white' : 'bg-light';
  const who = env.self ? '' : `<div class="small text-muted">${safeNick} · ${time}</div>`;
  const row = document.createElement('div');
  row.className = `py-1 ${rowClass}`;
  row.innerHTML = `
    ${who}
    <span class="d-inline-block px-2 py-1 rounded ${bubbleClass}" style="max-width:85%; word-break:break-word; text-align:left;">
      ${safeText}
    </span>
    ${env.self ? `<div class="small text-muted">${time}</div>` : ''}
  `;
  el.messageLog.appendChild(row);
  el.messageLog.scrollTop = el.messageLog.scrollHeight;
}

// ---------- utilities ----------

function _esc(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

function _ago(ts) {
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (s < 5)   return 'just now';
  if (s < 60)  return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

export default { initNearbyPanel, showNearbyPanel, destroyNearbyPanel };
